import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { translations } from "@/i18n";
import { customerMe, customerLogout as apiLogout, getWishlist, addWishlistItem, removeWishlistItem, mergeWishlist } from "@/api";

const AppContext = createContext(null);
export const useApp = () => useContext(AppContext);

const WHATSAPP_NUMBER = "79263070808";

export const cartKey = (id, size) => (size ? `${id}__${size}` : id);
const availFor = (product, size) => {
  const ss = product.size_stock || {};
  if (size) return Object.keys(ss).length ? (ss[size] ?? 0) : (product.stock ?? 0);
  return product.stock ?? 0;
};

const loadStored = (key) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

const initialLang = () => {
  const urlLang = new URLSearchParams(window.location.search).get("lang");
  if (urlLang === "en" || urlLang === "ru") return urlLang;
  const stored = localStorage.getItem("hg_lang");
  return stored === "ru" ? "ru" : "en";
};

export const AppProvider = ({ children }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [lang, setLangState] = useState(initialLang);
  const [currency, setCurrency] = useState("USD");

  // Reflect language in the URL (?lang=) so search engines can index each
  // language version separately and hreflang alternates resolve to real URLs.
  const setLang = useCallback((next) => {
    setLangState(next);
    localStorage.setItem("hg_lang", next);
    const sp = new URLSearchParams(window.location.search);
    sp.set("lang", next);
    setSearchParams(sp, { replace: true });
  }, [setSearchParams]);

  // Ensure ?lang is present on first load so canonical/hreflang stay consistent.
  useEffect(() => {
    if (!searchParams.get("lang")) {
      const sp = new URLSearchParams(window.location.search);
      sp.set("lang", lang);
      setSearchParams(sp, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [cart, setCart] = useState(() =>
    loadStored("hg_cart").map((p) => ({ ...p, key: p.key || cartKey(p.id, p.size || null), quantity: p.quantity || 1 }))
  );
  const [wishlist, setWishlist] = useState(() => loadStored("hg_wishlist"));
  const [cartOpen, setCartOpen] = useState(false);
  const [wishlistOpen, setWishlistOpen] = useState(false);
  const [inquiryOpen, setInquiryOpen] = useState(false);
  const [customer, setCustomer] = useState(null);

  const t = useMemo(() => translations[lang], [lang]);

  useEffect(() => {
    localStorage.setItem("hg_cart", JSON.stringify(cart));
  }, [cart]);
  useEffect(() => {
    if (!customer) localStorage.setItem("hg_wishlist", JSON.stringify(wishlist));
  }, [wishlist, customer]);

  // Restore customer session on load (skip while returning from Google OAuth callback)
  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) return;
    if (!localStorage.getItem("hg_customer_token")) return;
    customerMe()
      .then(async (u) => {
        setCustomer(u);
        try { setWishlist(await getWishlist()); } catch { /* ignore */ }
      })
      .catch(() => localStorage.removeItem("hg_customer_token"));
  }, []);

  const loginCustomer = useCallback(async (data) => {
    if (data.token) localStorage.setItem("hg_customer_token", data.token);
    setCustomer(data.user);
    try {
      const localIds = (loadStored("hg_wishlist") || []).map((p) => p.id).filter(Boolean);
      setWishlist(await mergeWishlist(localIds));
      localStorage.removeItem("hg_wishlist");
    } catch { /* ignore */ }
  }, []);

  const logoutCustomer = useCallback(async () => {
    try { await apiLogout(); } catch { /* ignore */ }
    localStorage.removeItem("hg_customer_token");
    setCustomer(null);
    setWishlist([]);
  }, []);

  const addToCart = useCallback((product, size = null) => {
    const max = availFor(product, size);
    if (max <= 0) return;
    const key = cartKey(product.id, size);
    setCart((prev) => {
      const ex = prev.find((p) => p.key === key);
      if (ex) return prev.map((p) => (p.key === key ? { ...p, quantity: Math.min(max, (p.quantity || 1) + 1) } : p));
      return [...prev, { ...product, size: size || null, key, quantity: 1, maxStock: max }];
    });
    setCartOpen(true);
  }, []);

  const removeFromCart = useCallback((key) => {
    setCart((prev) => prev.filter((p) => p.key !== key));
  }, []);

  const updateQty = useCallback((key, delta) => {
    setCart((prev) =>
      prev.map((p) =>
        p.key === key ? { ...p, quantity: Math.max(1, Math.min(p.maxStock || 99, (p.quantity || 1) + delta)) } : p
      )
    );
  }, []);

  const inCart = useCallback((id) => cart.some((p) => p.id === id), [cart]);

  const toggleWishlist = useCallback((product) => {
    if (customer) {
      const exists = wishlist.some((p) => p.id === product.id);
      setWishlist((prev) => (exists ? prev.filter((p) => p.id !== product.id) : [...prev, product]));
      (exists ? removeWishlistItem(product.id) : addWishlistItem(product.id))
        .then(setWishlist).catch(() => { /* ignore */ });
      return;
    }
    setWishlist((prev) =>
      prev.find((p) => p.id === product.id)
        ? prev.filter((p) => p.id !== product.id)
        : [...prev, product]
    );
  }, [customer, wishlist]);

  const removeFromWishlist = useCallback((id) => {
    if (customer) {
      setWishlist((prev) => prev.filter((p) => p.id !== id));
      removeWishlistItem(id).then(setWishlist).catch(() => { /* ignore */ });
      return;
    }
    setWishlist((prev) => prev.filter((p) => p.id !== id));
  }, [customer]);

  const inWishlist = useCallback((id) => wishlist.some((p) => p.id === id), [wishlist]);

  // Move a favourite into the selection (and out of the wishlist)
  const moveToCart = useCallback((product) => {
    const sizes = product.sizes || [];
    let size = null;
    if (sizes.length) {
      size = sizes.find((s) => availFor(product, s) > 0) || null;
      if (!size) { setWishlist((prev) => prev.filter((p) => p.id !== product.id)); return; }
    }
    const max = availFor(product, size);
    if (max <= 0) { setWishlist((prev) => prev.filter((p) => p.id !== product.id)); return; }
    const key = cartKey(product.id, size);
    setCart((prev) =>
      prev.find((p) => p.key === key)
        ? prev
        : [...prev, { ...product, size: size || null, key, quantity: 1, maxStock: max }]
    );
    setWishlist((prev) => prev.filter((p) => p.id !== product.id));
    setWishlistOpen(false);
    setCartOpen(true);
  }, []);

  const value = {
    lang, setLang,
    currency, setCurrency,
    cart, addToCart, removeFromCart, updateQty, inCart,
    wishlist, toggleWishlist, removeFromWishlist, inWishlist, moveToCart,
    cartOpen, setCartOpen,
    wishlistOpen, setWishlistOpen,
    inquiryOpen, setInquiryOpen,
    customer, loginCustomer, logoutCustomer, setCustomer,
    t,
    whatsappNumber: WHATSAPP_NUMBER,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};
